import { useEffect, useMemo, useState } from 'react';
import type { InclusionStatus, PhaseTwoMetadataRequest } from '../types';

type QType = 'text' | 'date' | 'choice' | 'number';

interface Question {
  id: string;
  label: string;
  help?: string;
  type: QType;
  required?: boolean;
  choices?: { value: string; label: string }[];
  autoAdvance?: boolean; // for choice
  mapTo: keyof PhaseTwoMetadataRequest;
  shownIf?: (answers: Partial<PhaseTwoMetadataRequest>) => boolean;
  validate?: (value: any) => boolean;
}

interface Props {
  defaults?: Partial<PhaseTwoMetadataRequest>;
  extractionDefaults?: Partial<PhaseTwoMetadataRequest>;
  onApply: (updates: Partial<PhaseTwoMetadataRequest>) => void;
  onComplete: () => void;
  onTitleChange?: (title: string, help?: string) => void;
}

const EVENT_TYPES = [
  'Neighborhood Bar',
  'Standard Cocktail Lounge',
  'Festival / Ticketed Event',
  'Stadium / Arena Pricing',
  'Ultra-Premium / VIP Event'
];

const normalizeDate = (v?: string) => {
  if (!v) return '';
  const iso = /^\d{4}-\d{2}-\d{2}$/;
  if (iso.test(v)) return v;
  const p = Date.parse(v);
  if (Number.isNaN(p)) return '';
  return new Date(p).toISOString().split('T')[0];
};

const QuestionEngine = ({ defaults, extractionDefaults, onApply, onComplete, onTitleChange }: Props) => {
  const initial = useMemo<Partial<PhaseTwoMetadataRequest>>(() => ({
    eventName: (defaults as any)?.eventName ?? (extractionDefaults as any)?.eventName ?? '',
    eventType: defaults?.eventType ?? extractionDefaults?.eventType,
    eventDate: normalizeDate(defaults?.eventDate ?? extractionDefaults?.eventDate),
    eventLocation: defaults?.eventLocation ?? extractionDefaults?.eventLocation ?? '',
    estimatedAttendees: (defaults as any)?.estimatedAttendees,
    taxIncluded: (defaults?.taxIncluded ?? extractionDefaults?.taxIncluded ?? 'NotApplicable') as InclusionStatus,
    taxRate: (defaults as any)?.taxRate ?? (extractionDefaults as any)?.taxRate,
    serviceFeeIncluded: (defaults?.serviceFeeIncluded ?? extractionDefaults?.serviceFeeIncluded ?? 'NotApplicable') as InclusionStatus,
    serviceFeeRate: (defaults as any)?.serviceFeeRate ?? (extractionDefaults as any)?.serviceFeeRate
  }), [defaults, extractionDefaults]);

  const [answers, setAnswers] = useState<Partial<PhaseTwoMetadataRequest>>(initial);
  const [index, setIndex] = useState(0);

  // Define the questions sequence
  const questions: Question[] = useMemo(() => [
    { id: 'eventName', label: 'Event name', type: 'text', required: true, mapTo: 'eventName' },
    { id: 'eventDate', label: 'Event date', type: 'date', required: true, mapTo: 'eventDate', help: "We'll use the date to analyze similar events in your area, around your date." },
    { id: 'eventLocation', label: 'Event location', type: 'text', required: true, mapTo: 'eventLocation', help: 'So we can analyze similar events in your area.' },
    { id: 'estimatedAttendees', label: 'Estimated attendees', type: 'number', mapTo: 'estimatedAttendees', help: 'How many attendees do you expect?', validate: (v) => v === '' || (!Number.isNaN(Number(v)) && Number(v) >= 0 && Number.isInteger(Number(v))) },
    { id: 'eventType', label: 'Event type', type: 'choice', mapTo: 'eventType', help: "Here's what we think the type of your event is, feel free to select what you think suits your event the best", choices: EVENT_TYPES.map(v => ({ value: v, label: v })) },
    { id: 'taxIncluded', label: 'Do your prices include tax?', type: 'choice', required: true, mapTo: 'taxIncluded', autoAdvance: false, help: 'Do your prices already include tax, or should we add it on top?',
      choices: [
        { value: 'NotApplicable', label: 'Not applicable' },
        { value: 'Included', label: 'Included' },
        { value: 'Additional', label: 'Additional' }
      ]
    },
    { id: 'serviceFeeIncluded', label: 'Do your prices include a service charge?', type: 'choice', required: true, mapTo: 'serviceFeeIncluded', autoAdvance: false, help: 'Do your prices already include a service charge, or do you add one on top?',
      choices: [
        { value: 'NotApplicable', label: 'Not applicable' },
        { value: 'Included', label: 'Included' },
        { value: 'Additional', label: 'Additional' }
      ]
    },
    // Rates captured inline with applicability question
  ], []);

  const visibleQuestions = questions.filter(q => !q.shownIf || q.shownIf(answers));
  const total = visibleQuestions.length;
  const current = visibleQuestions[index] ?? null;

  useEffect(() => {
    // When applicability changes, clear dependent rates
    if (answers.taxIncluded === 'NotApplicable') {
      setAnswers(a => ({ ...a, taxRate: undefined }));
    }
    if (answers.serviceFeeIncluded === 'NotApplicable') {
      setAnswers(a => ({ ...a, serviceFeeRate: undefined }));
    }
  }, [answers.taxIncluded, answers.serviceFeeIncluded]);

  if (!current) return null;

  // Announce current question to outer layout
  useEffect(() => {
    if (current) {
      onTitleChange?.(current.label, current.help);
    }
  }, [current?.id]);

  const setValue = (q: Question, value: any) => {
    setAnswers(prev => ({ ...prev, [q.mapTo]: value }));
    if (q.type === 'choice' && q.autoAdvance) {
      // apply partial and advance
      onApply({ [q.mapTo]: value } as any);
      // If setting to NotApplicable, also clear rate
      if (q.mapTo === 'taxIncluded' && value === 'NotApplicable') onApply({ taxRate: undefined });
      if (q.mapTo === 'serviceFeeIncluded' && value === 'NotApplicable') onApply({ serviceFeeRate: undefined });
      setTimeout(() => setIndex(i => Math.min(i + 1, visibleQuestions.length - 1)), 0);
    }
  };

  const isValid = () => {
    const v = (answers as any)[current.mapTo];
    if (current.type === 'number') {
      return current.validate ? current.validate(String(v ?? '')) : true;
    }
    if (current.required) {
      return v != null && String(v).trim().length > 0;
    }
    return true;
  };

  const goNext = () => {
    // Apply current answer before moving, including inline rates if relevant
    const payload: any = { [current.mapTo]: (answers as any)[current.mapTo] };
    if (current.mapTo === 'taxIncluded') {
      payload.taxRate = (answers as any).taxRate;
      if ((answers as any).taxIncluded === 'NotApplicable') payload.taxRate = undefined;
    }
    if (current.mapTo === 'serviceFeeIncluded') {
      payload.serviceFeeRate = (answers as any).serviceFeeRate;
      if ((answers as any).serviceFeeIncluded === 'NotApplicable') payload.serviceFeeRate = undefined;
    }
    if (current.mapTo === 'estimatedAttendees') {
      const raw = (answers as any).estimatedAttendees;
      payload.estimatedAttendees = raw === '' || raw == null ? undefined : Number(raw);
    }
    onApply(payload);
    if (index >= visibleQuestions.length - 1) {
      // Finalize: also normalize NotApplicable rates
      const updates: Partial<PhaseTwoMetadataRequest> = { ...answers };
      if (updates.taxIncluded === 'NotApplicable') updates.taxRate = undefined;
      if (updates.serviceFeeIncluded === 'NotApplicable') updates.serviceFeeRate = undefined;
      onApply(updates);
      onComplete();
      return;
    }
    setIndex(i => i + 1);
  };

  const goBack = () => {
    setIndex(i => Math.max(0, i - 1));
  };

  return (
    <div className="theme-card p-8 bg-white">
      <div className="mb-4 flex items-center justify-end">
        <div className="h-1 w-40 rounded-full bg-slate-200">
          <div className="h-1 rounded-full bg-slate-600 transition-all" style={{ width: `${((index + 1) / total) * 100}%` }} />
        </div>
      </div>

      {/* Subtle in-card question heading */}
      <div className="space-y-1">
        <h3 className="text-base font-semibold text-slate-900">{current.label}</h3>
        {current.help && (
          <p className="text-xs text-slate-600">{current.help}</p>
        )}
      </div>

      <div className="mt-6">
        {current.type === 'text' && (
          <input
            autoFocus
            type="text"
            value={(answers as any)[current.mapTo] ?? ''}
            onChange={(e) => setValue(current, e.target.value)}
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
          />
        )}
        {current.type === 'date' && (
          <input
            autoFocus
            type="date"
            value={(answers as any)[current.mapTo] ?? ''}
            onChange={(e) => setValue(current, e.target.value)}
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
          />
        )}
        {current.type === 'number' && (
          <input
            autoFocus
            type="number"
            min={0}
            step={current.mapTo === 'estimatedAttendees' ? 1 : 0.1}
            value={(answers as any)[current.mapTo] ?? ''}
            onChange={(e) => setValue(current, e.target.value)}
            placeholder={current.mapTo === 'estimatedAttendees' ? 'e.g., 500' : undefined}
            className="w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
          />
        )}
        {current.type === 'choice' && (
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-3">
              {(current.choices ?? []).map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setValue(current, c.value)}
                  className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition ${((answers as any)[current.mapTo] === c.value) ? 'border-slate-800 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-900 hover:bg-slate-50'}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {(current.mapTo === 'taxIncluded' && ((answers as any).taxIncluded === 'Included' || (answers as any).taxIncluded === 'Additional')) && (
              <div className="max-w-sm">
                <label className="flex flex-col gap-1 text-left">
                  <span className="text-sm font-medium text-slate-700">Tax rate (%)</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={(answers as any).taxRate ?? ''}
                    onChange={(e) => setAnswers(prev => ({ ...prev, taxRate: e.target.value === '' ? undefined : Number(e.target.value) }))}
                    placeholder="e.g., 10.1"
                    className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
                  />
                </label>
              </div>
            )}
            {(current.mapTo === 'serviceFeeIncluded' && ((answers as any).serviceFeeIncluded === 'Included' || (answers as any).serviceFeeIncluded === 'Additional')) && (
              <div className="max-w-sm">
                <label className="flex flex-col gap-1 text-left">
                  <span className="text-sm font-medium text-slate-700">Service charge rate (%)</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step={0.1}
                    value={(answers as any).serviceFeeRate ?? ''}
                    onChange={(e) => setAnswers(prev => ({ ...prev, serviceFeeRate: e.target.value === '' ? undefined : Number(e.target.value) }))}
                    placeholder="e.g., 18"
                    className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
                  />
                </label>
              </div>
            )}
          </div>
        )}
        
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        {index > 0 && (
          <button type="button" className="btn-secondary" onClick={goBack}>Back</button>
        )}
        <button type="button" className="btn-primary" disabled={!isValid()} onClick={goNext}>Next</button>
      </div>
    </div>
  );
};

export default QuestionEngine;
